import React, { useCallback, useEffect, useRef, useState } from "react";
import { Drawer, Button, Input, Tooltip, message } from "antd";
import {
  RobotOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  FileAddOutlined,
  LoadingOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ReportService, {
  AiChatMessage,
  AiReportCaseContext,
} from "../../../services/Report/report.service";
import { renderMarkdown } from "./markdown";
import "./radAiAssistant.scss";

interface RadAiAssistantProps {
  visible: boolean;
  onClose: () => void;
  userRole?: string;
  caseDetail?: AiReportCaseContext;
  /** Returns the current report editor HTML at send time (grounding context). */
  getReportContent: () => string;
  /** Insert assistant content (rendered HTML) into the report editor. */
  onInsert: (html: string) => void;
}

const SUGGESTIONS = [
  "Summarize this case in 2 lines",
  "Give a differential for these findings",
  "Suggest the impression for this report",
  "What follow-up imaging is recommended?",
];

const RadAiAssistant: React.FC<RadAiAssistantProps> = ({
  visible,
  onClose,
  userRole,
  caseDetail,
  getReportContent,
  onInsert,
}) => {
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const abortRef = useRef<{ abort: () => void } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingText, visible, scrollToBottom]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const send = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (!text || isStreaming) return;
      const nextMessages: AiChatMessage[] = [
        ...messages,
        { role: "user", content: text },
      ];
      setMessages(nextMessages);
      setInput("");
      setIsStreaming(true);
      setStreamingText("");
      abortRef.current = ReportService.streamAiChat(
        nextMessages,
        {
          userRole,
          caseDetail,
          reportContent: getReportContent(),
        },
        {
          onDelta: (acc) => setStreamingText(acc),
          onDone: (finalText) => {
            setMessages((prev) => [
              ...prev,
              { role: "assistant", content: finalText },
            ]);
            setStreamingText("");
            setIsStreaming(false);
            abortRef.current = null;
          },
          onError: (msg) => {
            setIsStreaming(false);
            setStreamingText("");
            abortRef.current = null;
            message.error(msg || "AI assistant request failed");
          },
        }
      );
    },
    [messages, isStreaming, userRole, caseDetail, getReportContent]
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (streamingText.trim()) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: streamingText },
      ]);
    }
    setStreamingText("");
    setIsStreaming(false);
  }, [streamingText]);

  const clearChat = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setStreamingText("");
    setIsStreaming(false);
  }, []);

  const copyText = useCallback((text: string) => {
    const done = () => message.success("Copied");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => message.error("Copy failed"));
    } else {
      done();
    }
  }, []);

  const insertText = useCallback(
    (text: string) => {
      onInsert(renderMarkdown(text));
      message.success("Inserted into report");
    },
    [onInsert]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const isEmpty = messages.length === 0 && !streamingText;

  return (
    <Drawer
      className="radai-drawer"
      title={
        <div className="radai-drawer__title">
          <span className="radai-drawer__title-left">
            <span className="radai-drawer__avatar">
              <RobotOutlined />
            </span>
            <span>
              RadAI Assistant
              <small>Ask, discuss, and draft — grounded in this case</small>
            </span>
          </span>
          <span className="radai-drawer__title-actions">
            <Tooltip title="Clear conversation">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                disabled={isEmpty}
                onClick={clearChat}
              />
            </Tooltip>
            <Tooltip title="Close">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
              />
            </Tooltip>
          </span>
        </div>
      }
      placement="right"
      width={Math.min(460, typeof window !== "undefined" ? window.innerWidth : 460)}
      visible={visible}
      closable={false}
      onClose={onClose}
      bodyStyle={{ padding: 0, display: "flex", flexDirection: "column", height: "100%" }}
    >
      <div className="radai-body">
        <div className="radai-messages" ref={scrollRef}>
          {isEmpty ? (
            <div className="radai-empty">
              <div className="radai-empty__icon">
                <RobotOutlined />
              </div>
              <h3>How can I help with this case?</h3>
              <p>
                I can summarize the study, suggest differentials, draft the
                impression, or answer radiology questions.
              </p>
              <div className="radai-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    className="radai-suggestion"
                    onClick={() => send(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={`radai-msg radai-msg--${m.role}`}
                >
                  {m.role === "assistant" ? (
                    <>
                      <div
                        className="radai-md"
                        dangerouslySetInnerHTML={{
                          __html: renderMarkdown(m.content),
                        }}
                      />
                      <div className="radai-msg__actions">
                        <Tooltip title="Copy">
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => copyText(m.content)}
                          />
                        </Tooltip>
                        <Tooltip title="Insert into report">
                          <Button
                            type="text"
                            size="small"
                            icon={<FileAddOutlined />}
                            onClick={() => insertText(m.content)}
                          />
                        </Tooltip>
                      </div>
                    </>
                  ) : (
                    <div className="radai-msg__text">{m.content}</div>
                  )}
                </div>
              ))}
              {isStreaming && (
                <div className="radai-msg radai-msg--assistant">
                  {streamingText ? (
                    <div
                      className="radai-md"
                      dangerouslySetInnerHTML={{
                        __html:
                          renderMarkdown(streamingText) +
                          '<span class="radai-cursor"></span>',
                      }}
                    />
                  ) : (
                    <div className="radai-typing">
                      <span />
                      <span />
                      <span />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="radai-input">
          <Input.TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask RadAI anything about this case…  (Enter to send, Shift+Enter for new line)"
            autoSize={{ minRows: 1, maxRows: 6 }}
            disabled={isStreaming}
          />
          {isStreaming ? (
            <Button
              className="radai-send"
              type="default"
              icon={<LoadingOutlined />}
              onClick={stop}
            >
              Stop
            </Button>
          ) : (
            <Button
              className="radai-send radai-send--icon"
              type="primary"
              shape="circle"
              icon={<ArrowUpOutlined />}
              disabled={!input.trim()}
              onClick={() => send(input)}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default RadAiAssistant;
